import {
  AppstoreOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Empty,
  Form,
  message,
  Modal,
  Pagination,
  Segmented,
  Select,
  Spin,
  Table,
} from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSendMediasToQualify } from '../../../../utils/api/medias/useSendMediasToQualify.jsx';
import MediaIconsGrid from './MediaIconsGrid';
import { ICONS_SORT_OPTIONS } from './mediaPath';
import { getMediaTableColumns } from './mediaTableColumns';

/** @typedef {{ label: string, value: string }} QualifyTagOption */

/**
 * @param {object} props
 * @param {Array<QualifyTagOption | string>} [props.availableTags] — Tags suggérés (libellé = valeur si chaîne).
 * @param {(tag: string) => void | Promise<void>} [props.onCreateTag] — Appelé pour chaque valeur ajoutée qui n’est pas dans `availableTags`.
 */
const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

// Contenu du dossier courant façon explorateur : dossiers puis fichiers.
// Seuls les fichiers sont sélectionnables pour l'envoi en qualification.
const MediaFilesTable = ({
  dataSource,
  folderCount,
  selectedRowKeys,
  onSelectionChange,
  focusedKey,
  onRowClick,
  onRowOpen,
  pagination,
  onPageChange,
  onTableChange,
  filterBy,
  onFilterChange,
  qualificationFilterBy,
  onQualificationFilterChange,
  isLoading,
  notFound,
  isSearching,
  getRowMenuItems,
  onRowMenuClick,
  notFoundAction,
  viewMode = 'details',
  onViewModeChange,
  sortValue,
  onSortChange,
  availableTags = [],
  onCreateTag,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  // Menu du clic droit, affiché à la position du curseur.
  const [rowMenu, setRowMenu] = useState(null);
  const [form] = Form.useForm();
  const tagsSelectionRef = useRef([]);
  const columns = getMediaTableColumns();

  const tagSelectOptions = useMemo(
    () =>
      (availableTags ?? []).map((t) =>
        typeof t === 'string'
          ? { label: t, value: t }
          : { label: t.label, value: t.value }
      ),
    [availableTags]
  );

  const availableValueSet = useMemo(
    () => new Set(tagSelectOptions.map((o) => String(o.value))),
    [tagSelectOptions]
  );

  useEffect(() => {
    if (isModalVisible) {
      form.resetFields();
      tagsSelectionRef.current = [];
    }
  }, [isModalVisible, form]);

  const { sendMediasToQualify, isLoading: isSending } = useSendMediasToQualify({
    onSuccess: (data) => {
      const hasErrors = Array.isArray(data.errors) && data.errors.length > 0;

      if (hasErrors) {
        data.errors.forEach((error) => {
          message.error(error);
        });
      }

      if (data.medias && data.medias.length > 0) {
        message.success(
          `${data.medias.length} média${data.medias.length > 1 ? 's' : ''} envoyé${data.medias.length > 1 ? 's' : ''} à qualifier avec succès`
        );
      }

      if (!hasErrors) {
        setIsModalVisible(false);
        onSelectionChange([]);
      }
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || "Erreur lors de l'envoi des médias"
      );
    },
  });

  const handleSendToQualify = () => {
    setIsModalVisible(true);
  };

  const handleConfirmSend = async () => {
    try {
      const values = await form.validateFields();
      await sendMediasToQualify({
        filePaths: selectedRowKeys,
        tags: values.tags ?? [],
      });
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Error sending medias:', error);
    }
  };

  const handleFormValuesChange = (_, allValues) => {
    const next = allValues.tags ?? [];
    const prev = tagsSelectionRef.current;
    const added = next.filter((v) => !prev.includes(v));
    added.forEach((v) => {
      const key = String(v);
      if (!availableValueSet.has(key) && typeof onCreateTag === 'function') {
        void Promise.resolve(onCreateTag(key));
      }
    });
    tagsSelectionRef.current = next;
  };

  const handleCancelModal = () => {
    setIsModalVisible(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
    getCheckboxProps: (record) => ({
      disabled: record.isFolder,
      'aria-label': `Sélectionner ${record.name}`,
    }),
    renderCell: (_checked, record, _index, node) =>
      record.isFolder ? null : node,
  };

  const openRowMenu = (record, event) => {
    const items = getRowMenuItems?.(record);
    if (!items?.length) return;
    event.preventDefault();
    onRowClick(record, event);
    setRowMenu({ record, items, x: event.clientX, y: event.clientY });
  };

  const onRow = (record) => ({
    tabIndex: 0,
    onClick: (event) => {
      // La case à cocher gère elle-même la sélection multiple.
      if (event.target.closest('.ant-table-selection-column')) return;
      onRowClick(record, event);
    },
    onDoubleClick: () => onRowOpen(record),
    onKeyDown: (event) => {
      if (event.key === 'Enter') onRowOpen(record);
    },
    onContextMenu: (event) => openRowMenu(record, event),
    style: { cursor: 'default', userSelect: 'none' },
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', marginTop: '100px' }}>
          <Spin size="large" tip="Chargement des médias..." />
        </div>
      );
    }

    if (notFound) {
      return (
        <Empty
          description="Ce dossier n'existe plus sur le disque média"
          style={{ marginTop: '100px' }}
        >
          {notFoundAction}
        </Empty>
      );
    }

    if (dataSource.length === 0) {
      return (
        <Empty
          description={
            isSearching
              ? 'Aucun élément ne correspond à la recherche'
              : 'Ce dossier est vide'
          }
          style={{ marginTop: '100px' }}
        />
      );
    }

    return (
      <>
        {viewMode === 'icons' ? (
          <MediaIconsGrid
            dataSource={dataSource}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={onSelectionChange}
            focusedKey={focusedKey}
            onRowClick={onRowClick}
            onRowOpen={onRowOpen}
            onRowContextMenu={openRowMenu}
          />
        ) : (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            onRow={onRow}
            rowClassName={(record) =>
              record.key === focusedKey ? 'ant-table-row-selected' : ''
            }
            onChange={onTableChange}
            scroll={{ y: 'calc(100vh - 400px)' }}
            size="middle"
            style={{ flex: 1 }}
          />
        )}
        {pagination.total > 0 && (
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={onPageChange}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            showTotal={(total, range) =>
              `Fichiers ${range[0]}-${range[1]} sur ${total}`
            }
            style={{ marginTop: 8, textAlign: 'right' }}
          />
        )}
      </>
    );
  };

  return (
    <div
      style={{
        padding: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: 16,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Select
            value={filterBy}
            onChange={onFilterChange}
            style={{ width: 150 }}
            options={[
              { value: '', label: 'Tous les types' },
              { value: 'image', label: 'Images' },
              { value: 'video', label: 'Vidéos' },
              { value: 'document', label: 'Documents' },
            ]}
          />
        </div>
        <Select
          value={qualificationFilterBy}
          onChange={onQualificationFilterChange}
          style={{ width: 250 }}
          options={[
            { value: '', label: 'Tous' },
            { value: 'true', label: 'Médias en cours de qualification' },
            {
              value: 'false',
              label: 'Exclure les médias en cours de qualification',
            },
          ]}
        />
        {viewMode === 'icons' && (
          <Select
            aria-label="Trier par"
            value={sortValue}
            onChange={onSortChange}
            style={{ width: 240 }}
            options={ICONS_SORT_OPTIONS}
          />
        )}
        <Segmented
          value={viewMode}
          onChange={onViewModeChange}
          style={{ marginLeft: 'auto' }}
          options={[
            {
              value: 'details',
              label: 'Détails',
              icon: <UnorderedListOutlined aria-hidden />,
            },
            {
              value: 'icons',
              label: 'Grandes icônes',
              icon: <AppstoreOutlined aria-hidden />,
            },
          ]}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: 8,
          width: '100%',
        }}
      >
        <span style={{ fontSize: '14px', color: '#666' }}>
          {selectedRowKeys.length > 0
            ? `${selectedRowKeys.length} élément${selectedRowKeys.length > 1 ? 's' : ''} sélectionné${selectedRowKeys.length > 1 ? 's' : ''}`
            : `${plural(folderCount, 'dossier')} · ${plural(pagination.total, 'fichier')}`}
        </span>
        {selectedRowKeys.length > 0 && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            style={{ marginLeft: 'auto', backgroundColor: '#52c41a' }}
            onClick={handleSendToQualify}
            loading={isSending}
          >
            Envoyer à qualifier
          </Button>
        )}
      </div>
      {renderContent()}
      {rowMenu && (
        <Dropdown
          open
          menu={{
            items: rowMenu.items,
            onClick: ({ key }) => {
              onRowMenuClick?.(key, rowMenu.record);
              setRowMenu(null);
            },
          }}
          onOpenChange={(open) => !open && setRowMenu(null)}
          trigger={['contextMenu']}
        >
          <div
            style={{
              position: 'fixed',
              left: rowMenu.x,
              top: rowMenu.y,
              width: 1,
              height: 1,
            }}
          />
        </Dropdown>
      )}
      <Modal
        title="Confirmer l'envoi"
        open={isModalVisible}
        onOk={handleConfirmSend}
        onCancel={handleCancelModal}
        confirmLoading={isSending}
        okText="Confirmer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          onValuesChange={handleFormValuesChange}
          initialValues={{ tags: [] }}
        >
          <p style={{ marginBottom: 16 }}>
            Êtes-vous sûr de vouloir envoyer {selectedRowKeys.length} média
            {selectedRowKeys.length > 1 ? 's' : ''} à qualifier ?
          </p>
          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              placeholder="Sélectionnez des tags ou saisissez-en de nouveaux"
              options={tagSelectOptions}
              tokenSeparators={[',']}
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MediaFilesTable;
